import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import classNames from "classnames";

import RichTextBlock from "./RichTextBlock";
import InvisibleReCAPTCHA from "./InvisibleReCAPTCHA";
import DisclaimerCopy from "./DisclaimerCopy";

import { useSiteMetadata } from "../hooks/useSiteMetadata";
import {
    getFormattedPhoneNumber,
    isValidEmail,
    isValidPhoneNumber,
    isValidName,
} from "../utils/utils";

import "../styles/components/FacilitySupportForm.scss";

const FacilitySupportForm = ({ lead, submitId }) => {
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [facilityName, setFacilityName] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [message, setMessage] = useState("");
    const [privacyCheckbox, setPrivacyCheckbox] = useState(false);

    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [hasSubmitAttempt, setHasSubmitAttempt] = useState(false);
    const [errors, setErrors] = useState({});
    const [submissionError, setSubmissionError] = useState(null);

    const setValidity = (field, valid) =>
        setErrors({ ...errors, [field]: !valid });

    const updateEmail = e => {
        setEmail(e.target.value);
        setValidity("email", isValidEmail(e.target.value));
    };

    const updatePhoneNumber = e => {
        let number = getFormattedPhoneNumber(e.target.value);
        setPhoneNumber(number);
        setValidity("phoneNumber", isValidPhoneNumber(number));
    };

    const isValid = () => {
        const _errors = {
            facilityName: !isValidName(facilityName),
            firstName: !isValidName(firstName),
            lastName: !isValidName(lastName),
            email: !isValidEmail(email),
            phoneNumber: phoneNumber && !isValidPhoneNumber(phoneNumber),
        };
        setErrors(_errors);
        return !Object.keys(_errors).some(key => _errors[key]);
    };

    const {
        endpoints: { facilitySupport: endpointUrl },
    } = useSiteMetadata();

    const postFormData = async reCAPTCHAToken => {
        const formData = {
            facility_name: facilityName,
            first_name: firstName,
            last_name: lastName,
            phone: phoneNumber,
            email,
            message,
            recaptcha_token: reCAPTCHAToken,
            referer: window?.location?.href || "",
        };
        const response = await fetch(endpointUrl, {
            method: "POST",
            mode: "cors",
            cache: "no-cache",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(formData),
        });
        return response.json();
    };

    const submitForm = reCAPTCHAToken => {
        setSubmitting(true);
        if (reCAPTCHAToken && isValid()) {
            postFormData(reCAPTCHAToken)
                .then(data => {
                    setSubmissionError(data.error ? data.msg : null);
                    setSubmitted(true);
                })
                .catch(error => console.error(error));
        } else {
            setHasSubmitAttempt(true);
        }
        reCAPTCHARef.current.reset();
        setSubmitting(false);
    };

    const reCAPTCHARef = useRef(null);

    const executeReCAPTCHA = async e => {
        e.preventDefault();
        const token = await reCAPTCHARef.current.executeAsync();
        submitForm(token);
    };

    if (submitted && !submissionError) {
        return (
            <div className="alert-container">
                <Alert variant="success">
                    Thank you! A member of our team will be in touch soon.
                </Alert>
            </div>
        );
    }

    return (
        <Form
            className={classNames("facility-support-form", {
                "has-submit-attempt": hasSubmitAttempt,
            })}
            onSubmit={executeReCAPTCHA}
            key="facility-support-form"
        >
            {lead && (
                <div className="form-lead">
                    <RichTextBlock content={lead} />
                </div>
            )}
            <Form.Group
                className="form-submit-container align-items-center"
                as={Row}
            >
                <Col xs={12} className="mb-4">
                    <span className="required-label">
                        Required<sup>*</sup>
                    </span>
                </Col>
                <Col md={6} className="mb-4">
                    <Form.Control
                        type="text"
                        size="lg"
                        required
                        value={firstName}
                        onChange={e => setFirstName(e.target.value)}
                        placeholder="First Name*"
                        className={classNames({
                            invalid: !!errors["firstName"],
                        })}
                    />
                </Col>
                <Col md={6} className="mb-4">
                    <Form.Control
                        type="text"
                        size="lg"
                        required
                        value={lastName}
                        onChange={e => setLastName(e.target.value)}
                        placeholder="Last Name*"
                        className={classNames({
                            invalid: !!errors["lastName"],
                        })}
                    />
                </Col>

                <Col md={6} className="mb-4">
                    <Form.Control
                        type="email"
                        size="lg"
                        required
                        value={email}
                        onChange={updateEmail}
                        placeholder="Email*"
                        className={classNames({
                            invalid: !!errors["email"],
                        })}
                    />
                </Col>

                <Col md={6} className="mb-4">
                    <Form.Control
                        type="tel"
                        maxLength="14"
                        size="lg"
                        placeholder="Phone*"
                        onChange={updatePhoneNumber}
                        value={phoneNumber}
                        className={classNames({
                            invalid: !!errors["phoneNumber"],
                        })}
                    />
                </Col>

                <Col xs={12} className="mb-4">
                    <Form.Control
                        type="text"
                        size="lg"
                        required
                        value={facilityName}
                        onChange={e => setFacilityName(e.target.value)}
                        placeholder="Facility*"
                        className={classNames({
                            invalid: !!errors["facilityName"],
                        })}
                    />
                </Col>

                <Col xs={12} className="mb-4">
                    <Form.Control
                        as="textarea"
                        rows={3}
                        maxLength="255"
                        size="lg"
                        placeholder="Message"
                        onChange={e => setMessage(e.target.value)}
                        value={message}
                    />
                </Col>

                <Col xs={12} className="mb-4">
                    <Form.Check size="lg" type="checkbox">
                        <Form.Check.Input
                            type="checkbox"
                            required
                            checked={privacyCheckbox}
                            onChange={e => setPrivacyCheckbox(e.target.checked)}
                        />
                        <Form.Check.Label className="compliance ml-2">
                            <DisclaimerCopy />
                        </Form.Check.Label>
                    </Form.Check>
                </Col>

                <Col md={8} className="mb-4">
                    <Button
                        id={submitId}
                        variant="primary"
                        type="submit"
                        size="lg"
                        disabled={submitting}
                        className="px-5"
                    >
                        Submit
                    </Button>
                </Col>
            </Form.Group>
            {submissionError && (
                <Alert variant="danger">{submissionError}</Alert>
            )}
            <InvisibleReCAPTCHA innerRef={reCAPTCHARef} />
        </Form>
    );
};

FacilitySupportForm.propTypes = {
    submitId: PropTypes.string,
    lead: PropTypes.shape({ raw: PropTypes.string }),
};

export default FacilitySupportForm;
